import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";

import { Box, Typography } from "@mui/material";
import CODE from "../../Static/Constants/StatusCodes";

import {
	SetDigitalTwinsColumns,
	SetDigitalTwinsConfigurationType,
	SetDigitalTwinsTabValue,
} from "../../Actions/DigitalTwinsList";

import Loader from "../../Helper/Loaders/Loader";

import {
	decryptTheParams,
	encryptTheParams,
} from "../../Helper/QueryParams/EncryptDecrypt";

import TextHyperLinkWithCopyIcon from "../../Helper/Operations/TextHyperLinkWithCopyIcon";

import { ThemeProperties } from "../../Theme/ThemeProperties";

import InventoryMain from "./InventoryMain";
import { batteryConfigurations } from "../../Static/Data/DigitalTwinList/BatteryConfigurations";
import { getPagesMetaData } from "../../Api/Api";
import { SetSessionExpired } from "../../Actions";
import DeviceIDLink from "../../Features/Table/GenericDraggableTable/Renderers/columnTypes/DeviceIDLink";
import Cookies from "universal-cookie";
import ProgressBarColumnView from "../../Features/Table/GenericDraggableTable/Renderers/columnTypes/ProgressBarColumnView";
import { getSOHColor } from "../../Helper/BatteryHealth/BatteryHealthHelper";
import { getColumnsConfig } from "../../Features/Table/GenericDraggableTable/GenericDraggableTableFuncations";
import { useTranslation } from "react-i18next";
import ScreenContainer from "../../Components/ScreenContainer";
import AppCache from "../../Helper/Common/AppCache";
import { ALL_VEHICLES_INDICATORS } from "../../Components/FieldStatusIndicator";
import { usePermissions } from "../../Context/PermissionContext";
import { FEATURE_CODES } from "../../Static/FeatureMapping";

export default function Inventory(props) {
	const { t } = useTranslation();
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const cookies = new Cookies();
	const permissions = AppCache.getItem('permissions') || [];
	const { hasAnyPermission } = usePermissions();
	// Same gate as the Digital Twin header's marker, so the two surfaces agree about who
	// can see operational status.
	const canViewOperationalStatus = hasAnyPermission(FEATURE_CODES.OPERATIONAL_STATUS, ['view', 'update']);
	const queryParams = decryptTheParams();
	const [pagesContent, setPagesContent] = useState({
		batteryID: "",
		partner: "",
		tabValue: 0,
		pageHeader: "",
		mounted: false,
	});

	const customRenderers = {
		"digital-twin-list-series-parallel": (record, config) => {
			const level = config?.seriesParallelConfigLevel
			const tableValue = level === "pack"
				? `${record?.seriesPacks}S / ${record?.parallelPacks}P`
				: level === "module"
					? `${record?.seriesModulesInPack}S / ${record?.parallelModulesInPack}P`
					: `${record?.seriesModulesInPack * record?.seriesCellsInModule}S / ${record?.parallelModulesInPack * record?.parallelCellsInModule}P`
			return <Typography variant="tableRow">{tableValue}</Typography>
		},

		// Bus number cell. Carries the operational status marker on its top-right
		// corner - attached here rather than in column meta, so enabling an org is just
		// the globalConfig flag. Renders nothing when that flag is off.
		// Only the All Vehicles tab has a device-id-link column, though this renderer
		// map is shared by every Inventory tab.
		'device-id-link': (record, config) => {
			return <DeviceIDLink
				record={record}
				config={config}
				indicators={canViewOperationalStatus ? ALL_VEHICLES_INDICATORS : undefined}
				onTextClick={() => onBatteryIDClick(record)}
			/>
		},

		// All Battery Packs tab: licence plate cell. Reuses DeviceIDLink, which already
		// renders plain defaultValue text when the field is null — that is exactly the
		// registry-only (unmapped) pack case, so those rows get "-" and no link for free.
		// disableActiveStatus suppresses the ping dot: a pack row has no ping status, so
		// the dot would always render grey and read as "offline".
		// disableLink is forced on when batteryID is missing, so a plate can never link
		// to an unresolvable Digital Twin.
		// Lands on the components tab rather than overview: the row is a pack, so the
		// pack list is what the user is following the link to see.
		'battery-pack-plate-link': (record, config) => {
			return <DeviceIDLink
				record={record}
				config={{ ...config, disableLink: !record?.batteryID }}
				disableActiveStatus
				onTextClick={() => onBatteryIDClick(record, 'components')}
			/>
		},

		'battery-config-link': (record, config) => {
			const value = record?.[config?.dataIndex];
			return <div className="renderID">{TextHyperLinkWithCopyIcon(value, "Config Name", onConfigIDClick, record)}</div>;
		},

		'progress-bar': (record, config) => {
			const value = record?.[config?.dataIndex];
			const decimalPlaces = config?.decimalPlaces
			const barColor = getSOHColor(value)
			const startProgressFrom = config?.startProgressFrom ? config?.startProgressFrom : 0
			const unitPostFix = config?.unitPostfix ? config?.unitPostfix : ""
			const defaultValue = config?.defaultValue ? config?.defaultValue : "Not Available"
			return <ProgressBarColumnView
				minProgress={startProgressFrom}
				defaultText={defaultValue}
				value={value ? value?.toFixed(decimalPlaces) : 0}
				barColor={barColor}
				record={record}
				config={config}
				unitPostFix={unitPostFix}
			/>
		},

		'iot-status-badge': (record, config) => {
			const value = record?.[config?.dataIndex];
			if (!value) return <Typography variant="tableRow">—</Typography>;
			const isUnderTesting = value === "under_testing";
			const isMapped = value === "mapped";
			let backgroundColor = "#F3F4F6";
			let color = "#374151";
			let label = "Unmapped";
			if (isUnderTesting) {
				backgroundColor = "#FFF3CD";
				color = "#92400E";
				label = "Under Testing";
			} else if (isMapped) {
				backgroundColor = "#E0E7FF";
				color = "#3730A3";
				label = "Mapped";
			}
			return (
				<Box
					component="span"
					sx={{
						display: "inline-block",
						px: 1.5,
						py: 0.25,
						borderRadius: "12px",
						backgroundColor,
						color,
						fontSize: 12,
						fontWeight: 500,
					}}
				>
					{label}
				</Box>
			);
		},

		'iot-test-result-badge': (record, config) => {
			const value = record?.[config?.dataIndex];
			if (!value) return <Typography variant="tableRow">—</Typography>;
			const isPassed = value === "passed";
			return (
				<Box
					component="span"
					sx={{
						display: "inline-block",
						px: 1.5,
						py: 0.25,
						borderRadius: "12px",
						backgroundColor: isPassed ? "#D1FAE5" : "#FEE2E2",
						color: isPassed ? "#065F46" : "#991B1B",
						fontSize: 12,
						fontWeight: 500,
					}}
				>
					{isPassed ? "Passed" : "Failed"}
				</Box>
			);
		},
	};

	useEffect(() => {
		if ('tabValue' in queryParams) {
			dispatch(SetDigitalTwinsTabValue(+queryParams['tabValue']));
		}
		getPageMeta();
	}, []);


	async function getPageMeta() {
		const pageName = 'Inventory'
		const res = await getPagesMetaData(pageName)
		let allowAddDigitalTwin = true
		let allowAddBatteryConfiguration = true
		let filterConfigurations
		let availableEmailDomains = []

		if (res?.responseStatus?.code === CODE.SESSION_EXPIRED) {
			SetSessionExpired(true);
			return null;
		}

		let pageData = {}
		if (res && res?.responseStatus?.code === CODE.SUCCESS) {
			pageData = res.response?.response
			const tablesData = pageData.metaInfo.table
			filterConfigurations = pageData.metaInfo?.filterConfigurations
			allowAddDigitalTwin = pageData.metaInfo?.allowAddDigitalTwin
			allowAddBatteryConfiguration = pageData.metaInfo.allowAddBatteryConfiguration
			availableEmailDomains = pageData.metaInfo.availableEmailDomains
			initializeTableColumns(tablesData)
		}

		setPagesContent({
			...pageData.metaInfo,
			metaInfo: pageData.metaInfo,
			filterConfigurations: filterConfigurations,
			allowAddDigitalTwin: allowAddDigitalTwin,
			allowAddBatteryConfiguration: allowAddBatteryConfiguration,
			availableEmailDomains,
			mounted: true
		})
	}

	// landingTab is the Digital Twin tab slug to open on. Defaults to overview, which is
	// what every vehicle-list link wants; All Battery Packs overrides it so a pack row
	// lands on the tab that actually shows the packs.
	function onBatteryIDClick(record, landingTab = 'overview') {

		let temp;
		temp = {
			...queryParams,
			batteryID: record.batteryID,
			deviceID: record.deviceID,
			batterySearchedGlobal: "false",
			prevLink: '/',
			navigateFromTab: true,
			tabValue: landingTab
		};

		encryptTheParams(temp, navigate, true);
	}

	function onConfigIDClick(record) {

		let temp = {
			...queryParams,
			configID: record?.id,
			isEdit: true,
			isView: true,
			configName: record?.configName,
			application: record?.application
		};
		const batteryConfig = batteryConfigurations?.find(item => item?.value === record?.application)
		dispatch(SetDigitalTwinsConfigurationType(batteryConfig?.value));
		encryptTheParams(temp, navigate, false, "/DigitalTwin/add-config");

	}

	const shouldShowActionMenu = (config) => {
		const requiredPermissions = config?.requiredPermissions ?? [];
		const userPermissions = permissions ? [...permissions] : [];
		return requiredPermissions.every(permission => userPermissions.includes(permission));

	};

	function initializeTableColumns(tablesData) {
		// POSITIONAL: each entry is addressed by the TAB_INDEX of its tab component, so the
		// order here must match the Tabs array in InventoryMain.jsx.
		// 0 All Vehicles, 1 Battery Configurations, 2 All Battery Packs, 3 Unmapped IOT.
		const columnsConfig = [
			{
				name: "Digital Twins Columns",
				columns: getColumnsConfig({
					tableColumns: tablesData.digitalTwinListTable?.columnsConfig,
					tableColumnOrder: tablesData.digitalTwinListTable?.columnOrdering || [],
					shouldShowActionMenu,
					customRenderers,
					t
				})
			},
			{
				name: "Battery Configurations Columns",
				columns: getColumnsConfig({
					tableColumns: tablesData.digitalTwinListBatteryConfigurationTable?.columnsConfig,
					tableColumnOrder: tablesData.digitalTwinListBatteryConfigurationTable?.columnOrdering || [],
					shouldShowActionMenu,
					customRenderers,
					t
				})
			},
			{
				name: "Battery Packs Columns",
				columns: getColumnsConfig({
					tableColumns: tablesData.batteryPackListTable?.columnsConfig,
					tableColumnOrder: tablesData.batteryPackListTable?.columnOrdering || [],
					shouldShowActionMenu,
					customRenderers,
					t
				})
			},
			{
				name: "Unmapped IOT Devices Columns",
				columns: getColumnsConfig({
					tableColumns: tablesData.unlinkedIotListTable?.columnsConfig,
					tableColumnOrder: tablesData.unlinkedIotListTable?.columnOrdering || [],
					shouldShowActionMenu,
					customRenderers,
					t
				})
			}
		]
		dispatch(SetDigitalTwinsColumns(columnsConfig));
	}

	if (!pagesContent.mounted) {
		return (
			<Box sx={{ height: "100vh", bgcolor: ThemeProperties.backgroundPurpleDarker }}>
				<Loader />
			</Box>
		)
	}

	return (
		<ScreenContainer pagesContent={pagesContent}>
			<div style={{ marginLeft: '-55px', }}>
				<InventoryMain pagesContent={pagesContent} permissions={permissions} />
			</div>
		</ScreenContainer>
	)
}
